import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, RotateCcw } from "lucide-react";
import { CompletionByDeviceKey, SupervisorTeamClient } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ActivityTemplateFilterField } from "../components/ActivityTemplateFilterField";
import {
  HomeworkStatusPill,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
  StatusPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { TeamTabs } from "../components/PortalNavigation";
import {
  TeamClinicFilter,
  TeamClinicUnavailableState,
} from "../components/TeamClinicFilter";
import {
  CaseloadMetricCard,
  CompletionByDeviceChart,
  HomeworkOverviewMetrics,
  ReportHelpTooltip,
  ReportTableSection,
  ReportWindowBanner,
  ValueEngagementMetrics,
} from "../components/ReportVisuals";
import { useActivityTemplateTypeLookup } from "../hooks/useActivityTemplateTypeLookup";
import { usePortalData } from "../hooks/usePortalData";
import { isActivatedClientStatus } from "../utils/clientStatus";
import { useClinicianAssistantTeamScopeFilters } from "../components/ClinicianAssistantTrigger";
import { formatPortalDate } from "../utils/dateTime";
import {
  uniqueSupervisorTeamClients,
  uniqueSupervisorTeamMembers,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

type TeamReportKey = "caseload" | "homework" | "device" | "value";
type TeamReportPeriod = "this-week" | "last-30-days" | "custom";

interface TeamReportDateFilter {
  period: TeamReportPeriod;
  startDate: string;
  endDate: string;
}

const reportTabs: readonly { key: TeamReportKey; label: string }[] = [
  { key: "caseload", label: "Caseload Insights" },
  { key: "homework", label: "Homework Overview" },
  { key: "value", label: "Value & Engagement" },
  { key: "device", label: "Completion by Device" },
];

const reportPeriodLabels: Record<TeamReportPeriod, string> = {
  "this-week": "This Week",
  "last-30-days": "Last 30 Days",
  custom: "Custom",
};

const customPeriodReportKeys = new Set<TeamReportKey>(["homework", "device"]);

const terminalStatuses = new Set([
  "completed",
  "completed with modification",
  "attempted",
  "missed",
]);
const completedStatuses = new Set(["completed", "completed with modification"]);

function initialFilterValue(key: string): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(key)?.trim() ?? "";
}

function initialReport(): TeamReportKey {
  const value = initialFilterValue("report") as TeamReportKey;
  return reportTabs.some((tab) => tab.key === value) ? value : "caseload";
}

function initialReportDateFilters(
  reportKey: TeamReportKey,
): Record<TeamReportKey, TeamReportDateFilter> {
  const requestedPeriod = initialFilterValue("period");
  const period: TeamReportPeriod =
    requestedPeriod === "this-week" ||
    (requestedPeriod === "custom" && customPeriodReportKeys.has(reportKey))
      ? requestedPeriod
      : "last-30-days";
  const defaultFilter: TeamReportDateFilter = {
    period: "last-30-days",
    startDate: "",
    endDate: "",
  };

  return {
    caseload: { ...defaultFilter },
    homework: { ...defaultFilter },
    device: { ...defaultFilter },
    value: { ...defaultFilter },
    [reportKey]: {
      period,
      startDate: period === "custom" ? initialFilterValue("startDate") : "",
      endDate: period === "custom" ? initialFilterValue("endDate") : "",
    },
  };
}

function dateKey(value: string | Date): string | null {
  if (typeof value === "string") {
    const calendarDate = value.trim().match(/^(\d{4}-\d{2}-\d{2})$/);
    if (calendarDate) return calendarDate[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function shiftDate(value: string, days: number): string {
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

function reportWindow(
  period: TeamReportPeriod,
  startDate: string,
  endDate: string,
) {
  if (period === "custom") return { startDate, endDate };
  const windowEndDate =
    dateKey(new Date()) ?? new Date().toISOString().slice(0, 10);
  if (period === "last-30-days") {
    return {
      startDate: shiftDate(windowEndDate, -29),
      endDate: windowEndDate,
    };
  }
  const [year, month, day] = windowEndDate.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const daysSinceMonday = (weekday + 6) % 7;
  return {
    startDate: shiftDate(windowEndDate, -daysSinceMonday),
    endDate: windowEndDate,
  };
}

function isInWindow(
  value: string | null,
  window: { startDate: string; endDate: string },
): boolean {
  if (!value) return false;
  const key = dateKey(value);
  return Boolean(key && key >= window.startDate && key <= window.endDate);
}

function statusKey(value: string): string {
  return value.trim().toLowerCase();
}

function parseRoutineMinutes(value: string): number | null {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (/^\d+(?:\.\d+)?$/.test(normalized)) {
    return Number(normalized) / 60;
  }
  const clock = normalized.match(
    /^(\d+):([0-5]?\d)(?::([0-5]?\d(?:\.\d+)?))?$/,
  );
  if (clock) {
    return clock[3]
      ? Number(clock[1]) * 60 + Number(clock[2]) + Number(clock[3]) / 60
      : Number(clock[1]) + Number(clock[2]) / 60;
  }
  const duration = normalized.match(
    /^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours))?\s*(?:(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes))?\s*(?:(\d+(?:\.\d+)?)\s*(?:s|sec|secs|second|seconds))?$/,
  );
  if (!duration || !/[0-9]/.test(normalized)) return null;
  return (
    Number(duration[1] || 0) * 60 +
    Number(duration[2] || 0) +
    Number(duration[3] || 0) / 60
  );
}

function hasMeaningfulOutcome(
  value: string,
  negativeValues: readonly string[],
) {
  const normalized = value.trim().toLowerCase();
  return Boolean(normalized && !negativeValues.includes(normalized));
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "–";
  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);
  const remaining = rounded % 60;
  return minutes
    ? `${minutes}m${remaining ? ` ${remaining}s` : ""}`
    : `${remaining}s`;
}

function average(values: number[]): number | null {
  return values.length
    ? values.reduce((total, value) => total + value, 0) / values.length
    : null;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function normalizeDevice(value: string): {
  key: CompletionByDeviceKey;
  label: string;
} {
  const normalized = value.trim().toLowerCase();
  if (
    ["desktop_laptop", "desktop/laptop", "desktop", "laptop"].includes(
      normalized,
    )
  ) {
    return { key: "desktop_laptop", label: "Desktop/Laptop" };
  }
  if (["iphone", "ios_phone", "ios phone"].includes(normalized)) {
    return { key: "iphone", label: "iPhone" };
  }
  if (["android_phone", "android phone"].includes(normalized)) {
    return { key: "android_phone", label: "Android Phone" };
  }
  if (normalized === "ipad") return { key: "ipad", label: "iPad" };
  if (["android_tablet", "android tablet"].includes(normalized)) {
    return { key: "android_tablet", label: "Android Tablet" };
  }
  return { key: "unknown", label: "Unknown" };
}

function clientLabel(client: SupervisorTeamClient): React.ReactNode {
  return (
    <span className="cp-client-summary">
      <strong>{client.nickname || "Unnamed client"}</strong>
      <small>{client.accessCode}</small>
    </span>
  );
}

export function TeamReportsPage({ clinicianCode }: { clinicianCode: string }) {
  const activityTypeByTemplateId = useActivityTemplateTypeLookup(clinicianCode);
  const initialReportKey = useMemo(initialReport, []);
  const initialDateFilters = useMemo(
    () => initialReportDateFilters(initialReportKey),
    [initialReportKey],
  );
  const [reportKey, setReportKey] = useState<TeamReportKey>(initialReportKey);
  const [dateFilters, setDateFilters] =
    useState<Record<TeamReportKey, TeamReportDateFilter>>(initialDateFilters);
  const [clinicId, setClinicId] = useState(() =>
    initialFilterValue("clinicId"),
  );
  const [therapistId, setTherapistId] = useState(() =>
    initialFilterValue("therapistId"),
  );
  const [patientId, setPatientId] = useState(() =>
    initialFilterValue("patientId"),
  );
  const [activityTemplateId, setActivityTemplateId] = useState(() =>
    initialFilterValue("activityTemplateId"),
  );
  const activeDateFilter = dateFilters[reportKey];
  const { period, startDate, endDate } = activeDateFilter;
  const supportsCustomPeriod = customPeriodReportKeys.has(reportKey);
  const customDatesValid =
    period !== "custom" ||
    (Boolean(startDate) && Boolean(endDate) && startDate <= endDate);

  const updateActiveDateFilter = (patch: Partial<TeamReportDateFilter>) => {
    setDateFilters((current) => ({
      ...current,
      [reportKey]: { ...current[reportKey], ...patch },
    }));
  };

  const resetFilters = () => {
    setClinicId("");
    setTherapistId("");
    setPatientId("");
    setActivityTemplateId("");
    updateActiveDateFilter({
      period: "last-30-days",
      startDate: "",
      endDate: "",
    });
  };

  useEffect(() => {
    const url = new URL(globalThis.window.location.href);
    reportKey === "caseload"
      ? url.searchParams.delete("report")
      : url.searchParams.set("report", reportKey);
    clinicId
      ? url.searchParams.set("clinicId", clinicId)
      : url.searchParams.delete("clinicId");
    therapistId
      ? url.searchParams.set("therapistId", therapistId)
      : url.searchParams.delete("therapistId");
    patientId
      ? url.searchParams.set("patientId", patientId)
      : url.searchParams.delete("patientId");
    reportKey === "device" || reportKey === "value"
      ? activityTemplateId
        ? url.searchParams.set("activityTemplateId", activityTemplateId)
        : url.searchParams.delete("activityTemplateId")
      : url.searchParams.delete("activityTemplateId");
    period === "last-30-days"
      ? url.searchParams.delete("period")
      : url.searchParams.set("period", period);
    if (period === "custom") {
      startDate
        ? url.searchParams.set("startDate", startDate)
        : url.searchParams.delete("startDate");
      endDate
        ? url.searchParams.set("endDate", endDate)
        : url.searchParams.delete("endDate");
    } else {
      url.searchParams.delete("startDate");
      url.searchParams.delete("endDate");
    }
    globalThis.window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}`,
    );
  }, [
    activityTemplateId,
    clinicId,
    endDate,
    patientId,
    period,
    reportKey,
    startDate,
    therapistId,
  ]);

  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamReports(
        clinicianCode,
        clinicId,
        signal,
      ),
    [clinicId, clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist?.role === "supervisor",
  );
  const clinicOptions = data?.clinicOptions ?? data?.clinics ?? [];

  const effectiveClinicId = clinicOptions.some(
    (clinic) => clinic.id === clinicId,
  )
    ? clinicId
    : "";
  const staleClinic = Boolean(data && clinicId && !effectiveClinicId);
  const uniqueTeamMembers = useMemo(
    () => uniqueSupervisorTeamMembers(data?.teamMembers ?? []),
    [data?.teamMembers],
  );
  const effectiveTherapistId = uniqueTeamMembers.some(
    (member) => member.id === therapistId,
  )
    ? therapistId
    : "";
  useClinicianAssistantTeamScopeFilters({
    clinicId: effectiveClinicId,
    therapistId: effectiveTherapistId,
  });
  const clinicianOptions = useMemo(
    () =>
      uniqueTeamMembers.filter(
        (member) =>
          !effectiveClinicId ||
          member.memberships.some(
            (membership) => membership.clinicId === effectiveClinicId,
          ),
      ),
    [effectiveClinicId, uniqueTeamMembers],
  );
  const clientOptions = useMemo(
    () =>
      uniqueSupervisorTeamClients(data?.clients ?? []).filter(
        (client) =>
          isActivatedClientStatus(client.status) &&
          (!effectiveClinicId || client.clinicId === effectiveClinicId) &&
          (!effectiveTherapistId ||
            client.assignedTherapists.some(
              (therapist) => therapist.id === effectiveTherapistId,
            )),
      ),
    [data?.clients, effectiveClinicId, effectiveTherapistId],
  );
  const effectivePatientId = clientOptions.some(
    (client) => client.id === patientId,
  )
    ? patientId
    : "";

  useEffect(() => {
    if (data && patientId && !effectivePatientId) {
      setPatientId("");
    }
  }, [data, effectivePatientId, patientId]);
  const visibleClients = useMemo(
    () =>
      clientOptions.filter(
        (client) => !effectivePatientId || client.id === effectivePatientId,
      ),
    [clientOptions, effectivePatientId],
  );
  const visibleClientIds = useMemo(
    () => new Set(visibleClients.map((client) => client.id)),
    [visibleClients],
  );
  const clientById = useMemo(
    () =>
      new Map(
        uniqueSupervisorTeamClients(data?.clients ?? []).map((client) => [
          client.id,
          client,
        ]),
      ),
    [data?.clients],
  );
  const memberNameById = useMemo(
    () =>
      new Map(
        uniqueTeamMembers.map((member) => [member.id, member.therapistName]),
      ),
    [uniqueTeamMembers],
  );
  const baseAssignments = useMemo(
    () =>
      (data?.assignments ?? []).filter(
        (assignment) =>
          visibleClientIds.has(assignment.patientId) &&
          (!effectiveTherapistId ||
            assignment.assignedTherapistIds.includes(effectiveTherapistId)),
      ),
    [data?.assignments, effectiveTherapistId, visibleClientIds],
  );
  const activityOptions = useMemo(() => {
    const activities = new Map<string, { id: string; name: string }>();
    for (const assignment of baseAssignments) {
      if (assignment.activityTemplateId) {
        activities.set(assignment.activityTemplateId, {
          id: assignment.activityTemplateId,
          name: assignment.homeworkName || "Unnamed activity",
        });
      }
    }
    return [...activities.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [baseAssignments]);
  const effectiveActivityTemplateId = activityOptions.some(
    (activity) => activity.id === activityTemplateId,
  )
    ? activityTemplateId
    : "";
  const activityAssignments = useMemo(
    () =>
      baseAssignments.filter(
        (assignment) =>
          !effectiveActivityTemplateId ||
          assignment.activityTemplateId === effectiveActivityTemplateId,
      ),
    [baseAssignments, effectiveActivityTemplateId],
  );
  const visibleMessages = useMemo(
    () =>
      (data?.messageEvents ?? []).filter((event) =>
        visibleClientIds.has(event.patientId),
      ),
    [data?.messageEvents, visibleClientIds],
  );
  const window = useMemo(
    () => reportWindow(period, startDate, endDate),
    [endDate, period, startDate],
  );

  const caseload = useMemo(() => {
    const activeClients = visibleClients.filter(
      (client) => client.status.trim().toLowerCase() === "activated",
    );
    const activeIds = new Set(activeClients.map((client) => client.id));
    const messageCounts = new Map<string, number>();
    for (const event of visibleMessages) {
      if (
        activeIds.has(event.patientId) &&
        isInWindow(event.occurredAt, window)
      ) {
        messageCounts.set(
          event.patientId,
          (messageCounts.get(event.patientId) ?? 0) + 1,
        );
      }
    }
    const completedByClient = new Map<
      string,
      { count: number; durations: number[] }
    >();
    for (const assignment of baseAssignments) {
      if (
        activeIds.has(assignment.patientId) &&
        completedStatuses.has(statusKey(assignment.completionStatus)) &&
        isInWindow(assignment.completedAt, window)
      ) {
        const current = completedByClient.get(assignment.patientId) ?? {
          count: 0,
          durations: [],
        };
        current.count += 1;
        const minutes = parseRoutineMinutes(assignment.routineTime);
        if (minutes !== null && minutes >= 0)
          current.durations.push(minutes * 60);
        completedByClient.set(assignment.patientId, current);
      }
    }
    const lastActivity = new Map<string, string>();
    for (const event of visibleMessages) {
      const current = lastActivity.get(event.patientId);
      if (!current || Date.parse(event.occurredAt) > Date.parse(current)) {
        lastActivity.set(event.patientId, event.occurredAt);
      }
    }
    for (const assignment of baseAssignments) {
      if (
        !completedStatuses.has(statusKey(assignment.completionStatus)) ||
        !assignment.completedAt
      ) {
        continue;
      }
      const current = lastActivity.get(assignment.patientId);
      if (
        !current ||
        Date.parse(assignment.completedAt) > Date.parse(current)
      ) {
        lastActivity.set(assignment.patientId, assignment.completedAt);
      }
    }
    const noActivity = activeClients.filter(
      (client) =>
        !messageCounts.has(client.id) && !completedByClient.has(client.id),
    );
    return {
      activeClients,
      messageRows: activeClients
        .filter((client) => messageCounts.has(client.id))
        .map((client) => ({ client, count: messageCounts.get(client.id) ?? 0 }))
        .sort((a, b) => b.count - a.count),
      homeworkRows: activeClients
        .filter((client) => completedByClient.has(client.id))
        .map((client) => {
          const value = completedByClient.get(client.id)!;
          const totalSeconds = value.durations.length
            ? value.durations.reduce((total, seconds) => total + seconds, 0)
            : null;
          return {
            client,
            count: value.count,
            totalSeconds,
            averageSeconds:
              totalSeconds === null
                ? null
                : totalSeconds / value.durations.length,
          };
        })
        .sort((a, b) => b.count - a.count),
      noActivityRows: noActivity.map((client) => {
        const last = lastActivity.get(client.id) ?? null;
        const lastKey = last ? dateKey(last) : null;
        const days = lastKey
          ? Math.floor(
              (Date.parse(`${window.endDate}T00:00:00Z`) -
                Date.parse(`${lastKey}T00:00:00Z`)) /
                86400000,
            )
          : null;
        return { client, lastActiveDate: lastKey, daysSinceActive: days };
      }),
    };
  }, [baseAssignments, visibleClients, visibleMessages, window]);

  const homework = useMemo(() => {
    const assignments = baseAssignments.filter((assignment) =>
      isInWindow(assignment.assignedAt, window),
    );
    const completed = assignments.filter((assignment) =>
      completedStatuses.has(statusKey(assignment.completionStatus)),
    );
    const open = assignments.filter(
      (assignment) =>
        !completedStatuses.has(statusKey(assignment.completionStatus)),
    );
    const copingCount = completed.filter((assignment) =>
      hasMeaningfulOutcome(assignment.copingSkills, [
        "none",
        "no",
        "false",
        "n/a",
        "not used",
        "no coping skill",
        "no coping skills",
        "[]",
        "{}",
        "null",
      ]),
    ).length;
    const alteredCount = completed.filter((assignment) =>
      hasMeaningfulOutcome(assignment.altered, [
        "none",
        "no",
        "false",
        "n/a",
        "not altered",
        "0",
        "null",
      ]),
    ).length;
    const paired = assignments.filter(
      (assignment) =>
        assignment.firstSuds !== null &&
        assignment.secondSuds !== null &&
        assignment.firstSuds >= 0 &&
        assignment.firstSuds <= 10 &&
        assignment.secondSuds >= 0 &&
        assignment.secondSuds <= 10,
    );
    const routineMinutes = assignments
      .map((assignment) => parseRoutineMinutes(assignment.routineTime))
      .filter((value): value is number => value !== null && value >= 0);
    return {
      assigned: assignments.length,
      completed: completed.length,
      open: open.length,
      overdue: open.filter((assignment) => {
        const due = assignment.dueDate ? dateKey(assignment.dueDate) : null;
        return Boolean(due && due < window.endDate);
      }).length,
      completionRate: assignments.length
        ? (completed.length * 100) / assignments.length
        : null,
      selfInitiated: assignments.filter(
        (assignment) => assignment.createdByTherapistId === null,
      ).length,
      copingCount,
      copingRate: completed.length
        ? (copingCount * 100) / completed.length
        : null,
      alteredCount,
      alteredRate: completed.length
        ? (alteredCount * 100) / completed.length
        : null,
      pairedCount: paired.length,
      averagePre: average(paired.map((assignment) => assignment.firstSuds!)),
      averagePost: average(paired.map((assignment) => assignment.secondSuds!)),
      averageChange: average(
        paired.map(
          (assignment) => assignment.secondSuds! - assignment.firstSuds!,
        ),
      ),
      routineCount: routineMinutes.length,
      averageRoutine: average(routineMinutes),
      medianRoutine: median(routineMinutes),
    };
  }, [baseAssignments, window]);

  const device = useMemo(() => {
    const completed = activityAssignments.filter(
      (assignment) =>
        completedStatuses.has(statusKey(assignment.completionStatus)) &&
        isInWindow(assignment.completedAt, window),
    );
    const counts = new Map<
      CompletionByDeviceKey,
      { label: string; count: number }
    >();
    for (const assignment of completed) {
      const normalized = normalizeDevice(assignment.deviceType);
      const current = counts.get(normalized.key) ?? {
        label: normalized.label,
        count: 0,
      };
      current.count += 1;
      counts.set(normalized.key, current);
    }
    const rows = [...counts].map(([key, value]) => ({
      key,
      label: value.label,
      count: value.count,
      percentage: completed.length ? (value.count * 100) / completed.length : 0,
    }));
    return { rows };
  }, [activityAssignments, window]);

  const valueEngagement = useMemo(() => {
    const assignments = activityAssignments.filter(
      (assignment) =>
        terminalStatuses.has(statusKey(assignment.completionStatus)) &&
        isInWindow(assignment.assignedAt, window),
    );
    const enriched = assignments.map((assignment) => ({
      assignment,
      client: clientById.get(assignment.patientId)!,
      valueLinked: Boolean(assignment.valueSupportedId),
      engaged: statusKey(assignment.completionStatus) !== "missed",
    }));
    const metric = (linked: boolean) => {
      const cohort = enriched.filter((row) => row.valueLinked === linked);
      const engaged = cohort.filter((row) => row.engaged).length;
      return {
        sampleSize: cohort.length,
        engaged,
        rate: cohort.length ? (engaged * 100) / cohort.length : null,
      };
    };
    return { rows: enriched, linked: metric(true), unlinked: metric(false) };
  }, [activityAssignments, clientById, window]);

  const clinicianNames = useCallback(
    (client: SupervisorTeamClient) =>
      client.assignedTherapists.map(
        (therapist) =>
          memberNameById.get(therapist.id) || therapist.therapistName,
      ),
    [memberNameById],
  );

  const clientColumns = useMemo<
    readonly PortalTableColumn<{ client: SupervisorTeamClient }>[]
  >(
    () => [
      {
        key: "client",
        header: "Client",
        initialWidth: 190,
        render: (row) => clientLabel(row.client),
      },
      {
        key: "clinician",
        header: "Clinician",
        initialWidth: 220,
        render: (row) => (
          <RelationshipPillList names={clinicianNames(row.client)} />
        ),
      },
      {
        key: "clinic",
        header: "Clinic",
        initialWidth: 280,
        render: (row) => (
          <RelationshipPillList names={[row.client.clinicName]} />
        ),
      },
    ],
    [clinicianNames],
  );

  function reportBody() {
    if (reportKey === "caseload") {
      const messageColumns: readonly PortalTableColumn<
        (typeof caseload.messageRows)[number]
      >[] = [
        ...clientColumns,
        {
          key: "messages",
          header: (
            <span className="cp-report-label-with-tooltip">
              Client Interactions
              <ReportHelpTooltip
                label="Client Interactions"
                definition="Number of messages the client typed or spoke to the OCD Homework Assistant during this reporting period."
                placement="bottom"
              />
            </span>
          ),
          headerLabel: "Client Interactions",
          initialWidth: 180,
          render: (row) => row.count,
        },
      ];
      const homeworkColumns: readonly PortalTableColumn<
        (typeof caseload.homeworkRows)[number]
      >[] = [
        ...clientColumns,
        {
          key: "completed",
          header: "Completed",
          initialWidth: 120,
          render: (row) => row.count,
        },
        {
          key: "total",
          header: "Total Time",
          initialWidth: 130,
          render: (row) => formatDuration(row.totalSeconds),
        },
        {
          key: "average",
          header: "Average Time",
          initialWidth: 140,
          render: (row) => formatDuration(row.averageSeconds),
        },
      ];
      const inactiveColumns: readonly PortalTableColumn<
        (typeof caseload.noActivityRows)[number]
      >[] = [
        ...clientColumns,
        {
          key: "status",
          header: "Status",
          initialWidth: 120,
          render: (row) => <StatusPill status={row.client.status} />,
        },
        {
          key: "last",
          header: "Last Active",
          initialWidth: 130,
          render: (row) => formatPortalDate(row.lastActiveDate),
        },
        {
          key: "days",
          header: "Days Since Active",
          initialWidth: 145,
          render: (row) => row.daysSinceActive ?? "–",
        },
      ];
      return (
        <div className="cp-caseload-report">
          <section aria-labelledby="team-caseload-engagement-heading">
            <div className="cp-content-header">
              <div>
                <h2 id="team-caseload-engagement-heading">
                  Client Engagement Across Team Clients —{" "}
                  {reportPeriodLabels[period]}
                </h2>
                <p>
                  Shows the most active team clients based on user messages and
                  completed homework. {caseload.activeClients.length} activated{" "}
                  {caseload.activeClients.length === 1 ? "client" : "clients"}{" "}
                  in scope.
                </p>
                <p className="cp-caseload-report__window">
                  Reporting window: {formatPortalDate(window.startDate)} –{" "}
                  {formatPortalDate(window.endDate)} · As of{" "}
                  {formatPortalDate(window.endDate)} · America/Los_Angeles
                </p>
              </div>
            </div>
            <div className="cp-caseload-activity-grid">
              <CaseloadMetricCard
                label="Clients Who Interacted with the OCD Homework Assistant"
                value={caseload.messageRows.length}
                tone="messages"
              />
              <CaseloadMetricCard
                label="Clients Who Completed Homework"
                value={caseload.homeworkRows.length}
                tone="homework"
              />
              <ReportTableSection
                className="cp-caseload-report-table"
                title="Most Active Clients in the OCD Homework Assistant"
                emptyMessage="No message activity during this period."
                columns={messageColumns}
                records={caseload.messageRows}
                getRecordKey={(row) => row.client.id}
              />
              <ReportTableSection
                className="cp-caseload-report-table"
                title="Top Clients by Completed Homework"
                emptyMessage="No completed homework during this period."
                columns={homeworkColumns}
                records={caseload.homeworkRows}
                getRecordKey={(row) => row.client.id}
              />
            </div>
          </section>
          <section aria-labelledby="team-caseload-no-activity-heading">
            <div className="cp-content-header">
              <div>
                <h2 id="team-caseload-no-activity-heading">
                  Clients With No Activity — {reportPeriodLabels[period]}
                </h2>
                <p>
                  Shows activated team clients with no tracked activity during
                  the selected period.
                </p>
              </div>
            </div>
            <div className="cp-caseload-no-activity-column">
              <div className="cp-caseload-kpi-grid cp-caseload-kpi-grid--single">
                <CaseloadMetricCard
                  label={
                    caseload.noActivityRows.length > 0
                      ? `Clients With No Activity ${reportPeriodLabels[period]}`
                      : "All eligible clients had activity."
                  }
                  value={caseload.noActivityRows.length}
                  tone={
                    caseload.noActivityRows.length > 0
                      ? "inactive"
                      : "inactive-clear"
                  }
                />
              </div>
              {caseload.noActivityRows.length > 0 ? (
                <ReportTableSection
                  className="cp-caseload-report-table"
                  title="Clients With No Activity"
                  emptyMessage="No client details are available."
                  columns={inactiveColumns}
                  records={caseload.noActivityRows}
                  getRecordKey={(row) => row.client.id}
                />
              ) : null}
            </div>
          </section>
        </div>
      );
    }

    if (reportKey === "homework") {
      return (
        <div className="cp-homework-overview__body">
          <ReportWindowBanner className="cp-homework-overview__window">
            Assigned-date window: {formatPortalDate(window.startDate)} through{" "}
            {formatPortalDate(window.endDate)} · As of{" "}
            {formatPortalDate(window.endDate)} (America/Los_Angeles)
          </ReportWindowBanner>
          {homework.assigned === 0 ? (
            <p className="cp-homework-overview__empty-cohort" role="status">
              No homework was assigned during this period. Rates and averages
              are shown as em dashes.
            </p>
          ) : null}
          <HomeworkOverviewMetrics
            summary={{
              assigned: homework.assigned,
              completed: homework.completed,
              open: homework.open,
              overdue: homework.overdue,
              completionRate: homework.completionRate,
              selfInitiated: homework.selfInitiated,
            }}
            clinicalMetrics={{
              copingSkillAppliedCount: homework.copingCount,
              copingSkillAppliedDenominator: homework.completed,
              copingSkillAppliedRate: homework.copingRate,
              alteredRoutineCount: homework.alteredCount,
              alteredRoutineDenominator: homework.completed,
              alteredRoutineRate: homework.alteredRate,
              pairedSudsCount: homework.pairedCount,
              averagePreSuds: homework.averagePre,
              averagePostSuds: homework.averagePost,
              averageSudsChange: homework.averageChange,
              routineTimeRecordedCount: homework.routineCount,
              averageRoutineTime: homework.averageRoutine,
              medianRoutineTime: homework.medianRoutine,
            }}
          />
        </div>
      );
    }

    if (reportKey === "device") {
      return (
        <div className="cp-device-report__body">
          <ReportWindowBanner className="cp-device-report__window">
            Completed-date window: {formatPortalDate(window.startDate)} through{" "}
            {formatPortalDate(window.endDate)} · As of{" "}
            {formatPortalDate(window.endDate)} (America/Los_Angeles)
          </ReportWindowBanner>
          <section className="cp-device-report-card">
            <header>
              <h3>Completed Assignments by Device</h3>
              <p>
                Count and share of completed homework recorded for each device
                type.
              </p>
            </header>
            <CompletionByDeviceChart
              rows={device.rows}
              emptyDescription="Try a different clinic, clinician, client, activity, or period."
            />
          </section>
        </div>
      );
    }

    const columns: readonly PortalTableColumn<
      (typeof valueEngagement.rows)[number]
    >[] = [
      {
        key: "client",
        header: "Client",
        initialWidth: 180,
        render: (row) => clientLabel(row.client),
      },
      {
        key: "clinician",
        header: "Clinician",
        initialWidth: 210,
        render: (row) => (
          <RelationshipPillList names={clinicianNames(row.client)} />
        ),
      },
      {
        key: "activity",
        header: "Homework Activity",
        initialWidth: 240,
        render: (row) => row.assignment.homeworkName || "–",
      },
      {
        key: "value",
        header: "Value Linked",
        initialWidth: 125,
        render: (row) => (row.valueLinked ? "Yes" : "No"),
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 175,
        render: (row) => (
          <HomeworkStatusPill status={row.assignment.completionStatus} />
        ),
      },
      {
        key: "assigned",
        header: "Assigned",
        initialWidth: 125,
        render: (row) => formatPortalDate(dateKey(row.assignment.assignedAt)),
      },
    ];
    return (
      <section className="cp-value-engagement-report cp-team-value-engagement-report">
        <div className="cp-value-engagement-report__header">
          <div>
            <h3>Impact of Values on Homework Engagement</h3>
            <p>
              Compare terminal homework outcomes with and without a linked
              client value.
            </p>
          </div>
        </div>
        <div className="cp-value-engagement-report__body">
          <ReportWindowBanner>
            Assigned-date window: {formatPortalDate(window.startDate)} through{" "}
            {formatPortalDate(window.endDate)} · As of{" "}
            {formatPortalDate(window.endDate)} (America/Los_Angeles)
          </ReportWindowBanner>
          <ValueEngagementMetrics
            linked={valueEngagement.linked}
            unlinked={valueEngagement.unlinked}
          />
          {valueEngagement.rows.length ? (
            <PortalDataTable
              caption="Team value and engagement assignments"
              columns={columns}
              records={valueEngagement.rows}
              getRecordKey={(row) => row.assignment.id}
            />
          ) : (
            <p className="cp-value-engagement-report__empty" role="status">
              No completed, attempted, or missed homework matches the selected
              filters and period.
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <ClinicianPortalLayout
      activeGlobalKey="team"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Team Reports"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        clinicianNameLoading={state.status === "loading"}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Home", path: "/clinician-portal" },
              {
                label: "Team",
                path: withTeamClinicScope("/clinician-portal/team", clinicId),
              },
              { label: "Reports" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide">
        <TeamTabs
          clinicianCode={clinicianCode}
          activeKey="reports"
          clinicId={clinicId}
        />
        {state.status === "loading" ? (
          <LoadingState label="Loading team reports" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Team reports are unavailable")} />
        ) : null}
        {available && data && staleClinic ? (
          <TeamClinicUnavailableState
            onViewAllClinics={() => {
              setClinicId("");
              setTherapistId("");
              setPatientId("");
            }}
          />
        ) : null}
        {available && data && !staleClinic ? (
          <section aria-label="Team reports">
            <div className="cp-content-header">
              <div>
                <p>
                  Review aggregate activity across the clinics you supervise.
                  These reports are read-only.
                </p>
              </div>
              <div className="cp-content-header__actions">
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  onClick={state.refresh}
                  disabled={state.status === "refreshing"}
                >
                  <RefreshCw aria-hidden="true" />
                  {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
            <nav
              className="cp-team-report-tabs"
              aria-label="Team report type"
              role="tablist"
            >
              {reportTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={reportKey === tab.key}
                  onClick={() => setReportKey(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
            <div
              className="cp-team-client-filters cp-team-report-filters"
              aria-label="Team report filters"
            >
              <TeamClinicFilter
                clinicOptions={clinicOptions}
                value={effectiveClinicId}
                onChange={(nextClinicId) => {
                  setClinicId(nextClinicId);
                  setTherapistId("");
                  setPatientId("");
                }}
              />
              <label>
                <span>Clinician</span>
                <select
                  className="cp-form-input"
                  value={effectiveTherapistId}
                  onChange={(event) => {
                    setTherapistId(event.currentTarget.value);
                    setPatientId("");
                  }}
                >
                  <option value="">All clinicians</option>
                  {clinicianOptions.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.therapistName}
                      {member.isCurrentUser ? " (You)" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Client</span>
                <select
                  className="cp-form-input"
                  value={effectivePatientId}
                  onChange={(event) => setPatientId(event.currentTarget.value)}
                >
                  <option value="">All clients</option>
                  {clientOptions.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.nickname || "Unnamed client"} ({client.accessCode}
                      )
                    </option>
                  ))}
                </select>
              </label>
              {reportKey === "device" || reportKey === "value" ? (
                <ActivityTemplateFilterField
                  id="team-report-activity-template"
                  value={effectiveActivityTemplateId}
                  options={activityOptions}
                  activityTypeByTemplateId={activityTypeByTemplateId}
                  onChange={setActivityTemplateId}
                />
              ) : null}
              <label>
                <span>
                  {reportKey === "homework"
                    ? "Assigned Period"
                    : reportKey === "device"
                      ? "Completed Period"
                      : "Period"}
                </span>
                <select
                  className="cp-form-input"
                  value={period}
                  onChange={(event) =>
                    updateActiveDateFilter({
                      period: event.currentTarget.value as TeamReportPeriod,
                    })
                  }
                >
                  <option value="this-week">This Week</option>
                  <option value="last-30-days">Last 30 Days</option>
                  {supportsCustomPeriod ? (
                    <option value="custom">Custom</option>
                  ) : null}
                </select>
              </label>
              {supportsCustomPeriod && period === "custom" ? (
                <>
                  <label>
                    <span>Start Date</span>
                    <input
                      className="cp-form-input"
                      type="date"
                      value={startDate}
                      onChange={(event) =>
                        updateActiveDateFilter({
                          startDate: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>End Date</span>
                    <input
                      className="cp-form-input"
                      type="date"
                      min={startDate || undefined}
                      value={endDate}
                      onChange={(event) =>
                        updateActiveDateFilter({
                          endDate: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </>
              ) : null}
              <button
                type="button"
                className="cp-button cp-button--secondary"
                onClick={resetFilters}
              >
                <RotateCcw aria-hidden="true" />
                Reset Filters
              </button>
            </div>
            {!customDatesValid ? (
              <EmptyState
                title="Choose a valid date range"
                description="Select a start date and an end date on or after the start date."
              />
            ) : visibleClients.length ? (
              reportBody()
            ) : (
              <EmptyState
                title="No team clients found"
                description="No clients match the selected clinic and clinician filters."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
